import { styled } from '@mui/material/styles';
import type { Virtualizer } from '@tanstack/react-virtual';
import { useContext, useMemo, useRef } from 'react';

import {
  ColRowHighlightContext,
  RowOffsetContext,
  useExportMode,
  useSwimlaneRootProps,
  useSwimlaneSlot,
} from '@/components/ui/swimlane/context';
import {
  CELL_GAP,
  COL_ROW_HIGHLIGHT_COLOR,
  GRID_GAP,
  GROUP_COL_INNER_GAP,
  ROW_HEADER_WIDTH,
} from '@/components/ui/swimlane/layout/constants';
import { SwimlaneRowHeaderResize } from './SwimlaneRowHeaderResize';

interface IRowHeaders {
  rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
}

function isGroupHeaderRow(h: unknown): boolean {
  return (
    h != null &&
    typeof h === 'object' &&
    'isGroupHeader' in h &&
    (h as { isGroupHeader?: boolean }).isGroupHeader === true
  );
}

export function RowHeaders({ rowVirtualizer }: IRowHeaders) {
  const { component: SlotRowHeader, props: rowHeaderSlotProps } = useSwimlaneSlot('rowHeader');
  const { component: SlotGroupRowHeader, props: groupRowHeaderSlotProps } =
    useSwimlaneSlot('groupRowHeader');
  const { rowHighlightIndex, setRowHighlightIndex } = useContext(ColRowHighlightContext);
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    rowAxisLabelText,
    rowHeaders,
    rowHeaderOrientation,
    rowHeadersWidth,
    isHeadersSticky,
    backgroundColor,
  } = useSwimlaneRootProps();
  const { isExporting } = useExportMode();
  const effectiveIsHeadersSticky = isHeadersSticky && !isExporting;
  const hasAxisLabelRow = typeof rowAxisLabelText === 'string' && effectiveIsHeadersSticky;
  const subRowHeaderWidth =
    rowHeaderOrientation === 'vertical' ? ROW_HEADER_WIDTH.vertical.value : rowHeadersWidth;

  // Precompute group structure once per rowHeaders change:
  // - groupHeaderIndices: set of all group header indices
  // - groupForIndex: maps each sub-row index to its parent group header index
  // - groupSubRowRange: maps each group header index to its sub-row range [firstSubRow, lastSubRow]
  const {
    groupHeaderIndices,
    groupForIndex,
    groupSubRowRange,
    hasGroupHeaders,
    parentHeaderIdByIndex,
  } = useMemo(() => {
    const indices = new Set<number>();
    const parentMap = new Map<number, number>();
    const rangeMap = new Map<number, { first: number; last: number }>();
    const parentIdMap = new Map<number, string>();

    let currentGroup = -1;
    for (let i = 0; i < rowHeaders.length; i++) {
      if (isGroupHeaderRow(rowHeaders[i])) {
        indices.add(i);
        currentGroup = i;
        rangeMap.set(i, { first: i + 1, last: i });
      } else if (currentGroup !== -1) {
        parentMap.set(i, currentGroup);
        const range = rangeMap.get(currentGroup);
        if (range) range.last = i;
        const id = rowHeaders[currentGroup]?.id;
        if (id !== undefined) parentIdMap.set(i, String(id));
      }
    }

    return {
      groupHeaderIndices: indices,
      groupForIndex: parentMap,
      groupSubRowRange: rangeMap,
      hasGroupHeaders: indices.size > 0,
      parentHeaderIdByIndex: parentIdMap,
    };
  }, [rowHeaders]);

  const groupColumnWidth = hasGroupHeaders ? ROW_HEADER_WIDTH.vertical.value : 0;
  const totalWidth =
    groupColumnWidth > 0
      ? groupColumnWidth + GROUP_COL_INNER_GAP.value + subRowHeaderWidth
      : subRowHeaderWidth;

  if (rowHeaders.length === 0) {
    return null;
  }

  const rowHighLightStyles = {
    backgroundColor: COL_ROW_HIGHLIGHT_COLOR,
    outline: `6px solid ${COL_ROW_HIGHLIGHT_COLOR}`,
    borderTopLeftRadius: '6px',
    borderBottomLeftRadius: '6px',
  };

  const virtualizedItems = rowVirtualizer.getVirtualItems();

  // Inject group header virtual items when their sub-rows are visible but the header is scrolled out
  const allVirtualItems = (() => {
    if (!hasGroupHeaders) return virtualizedItems;
    const indexSet = new Set(virtualizedItems.map((v) => v.index));
    const toInject: (typeof virtualizedItems)[number][] = [];

    for (const vItem of virtualizedItems) {
      if (groupHeaderIndices.has(vItem.index)) continue;
      const groupIdx = groupForIndex.get(vItem.index);
      if (groupIdx === undefined || indexSet.has(groupIdx)) continue;

      indexSet.add(groupIdx);
      const size = rowVirtualizer.options.estimateSize(groupIdx);
      let start = vItem.start;
      for (let i = vItem.index - 1; i >= groupIdx; i--) {
        start -= CELL_GAP.value + rowVirtualizer.options.estimateSize(i);
      }
      toInject.push({ index: groupIdx, key: groupIdx, start, end: start + size, size, lane: 0 });
    }

    if (toInject.length === 0) return virtualizedItems;
    return [...toInject, ...virtualizedItems].sort((a, b) => a.index - b.index);
  })();

  // Build sizeByIndex for O(1) lookups (replaces repeated allVirtualItems.find())
  const sizeByIndex = new Map<number, number>();
  for (const v of allVirtualItems) {
    sizeByIndex.set(v.index, v.size);
  }
  const getSize = (index: number) =>
    sizeByIndex.get(index) ?? rowVirtualizer.options.estimateSize(index);

  // Compute translateY offsets and total group header space
  const translateYOffset = new Map<number, number>();
  let totalGroupHeaderOffset = 0;
  if (hasGroupHeaders) {
    let precedingGroupsOffset = 0;
    for (let i = 0; i < rowHeaders.length; i++) {
      translateYOffset.set(i, precedingGroupsOffset);
      if (groupHeaderIndices.has(i)) {
        precedingGroupsOffset += getSize(i) + CELL_GAP.value;
      }
    }
    totalGroupHeaderOffset = precedingGroupsOffset;
  }

  const containerHeight = rowVirtualizer.getTotalSize() - totalGroupHeaderOffset;

  return (
    <Container
      isStickyHeaders={isHeadersSticky && !isExporting}
      backgroundColor={backgroundColor}
      ref={containerRef}
      style={{
        gridArea: 'rowH',
        width: `${totalWidth}px`,
        height: `${containerHeight}px`,
        left: `${hasAxisLabelRow ? `${GRID_GAP.value + ROW_HEADER_WIDTH.vertical.value}px` : '0px'}`,
      }}
    >
      {allVirtualItems.map((virtualRow) => {
        const rowHeaderData = rowHeaders[virtualRow.index];
        const isRowHighlighted = rowHighlightIndex === virtualRow.index;
        const isGroupHeader = groupHeaderIndices.has(virtualRow.index);
        const left = isGroupHeader
          ? 0
          : groupColumnWidth > 0
            ? groupColumnWidth + GROUP_COL_INNER_GAP.value
            : 0;
        const width = isGroupHeader ? groupColumnWidth : subRowHeaderWidth;

        let spanHeight = virtualRow.size;
        if (isGroupHeader) {
          const range = groupSubRowRange.get(virtualRow.index);
          if (range && range.last >= range.first) {
            let subRowsHeight = 0;
            for (let i = range.first; i <= range.last; i++) {
              if (i > range.first) subRowsHeight += CELL_GAP.value;
              subRowsHeight += getSize(i);
            }
            spanHeight = subRowsHeight;
          }
        }

        const translateY = virtualRow.start - (translateYOffset.get(virtualRow.index) ?? 0);

        return (
          <RowHeaderContainer
            key={virtualRow.key}
            style={{
              width: `${width}px`,
              minHeight: `${spanHeight}px`,
              transform: `translateY(${translateY}px)`,
              left: `${left}px`,
              ...(isRowHighlighted && !isGroupHeader ? rowHighLightStyles : {}),
              backgroundColor,
            }}
            onMouseEnter={() => !isGroupHeader && setRowHighlightIndex(virtualRow.index)}
            onMouseLeave={() => !isGroupHeader && setRowHighlightIndex(-1)}
          >
            <RowOffsetContext.Provider
              value={{
                offset: translateY,
                height: spanHeight,
                container: rowVirtualizer.scrollElement || null,
              }}
            >
              {rowHeaderData && isGroupHeader && SlotGroupRowHeader && (
                <SlotGroupRowHeader
                  {...groupRowHeaderSlotProps}
                  {...rowHeaderData}
                  id={String(rowHeaderData.id)}
                  forceVertical
                />
              )}
              {rowHeaderData && !isGroupHeader && (
                <SlotRowHeader
                  {...rowHeaderSlotProps}
                  {...rowHeaderData}
                  id={String(rowHeaderData.id)}
                  parentHeaderId={parentHeaderIdByIndex.get(virtualRow.index)}
                />
              )}
            </RowOffsetContext.Provider>
          </RowHeaderContainer>
        );
      })}
      <SwimlaneRowHeaderResize
        rowHeaderWidth={totalWidth}
        groupColumnOffset={groupColumnWidth > 0 ? groupColumnWidth + GROUP_COL_INNER_GAP.value : 0}
        offset={containerRef.current?.getBoundingClientRect().left || 0}
        rowHeadersContainer={containerRef.current}
        scrollContainer={rowVirtualizer.scrollElement}
      />
    </Container>
  );
}

const RowHeaderContainer = styled('div')({
  position: 'absolute',
});

const Container = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isStickyHeaders' && prop !== 'backgroundColor',
})<{
  isStickyHeaders: boolean;
  backgroundColor?: string;
}>(({ isStickyHeaders, backgroundColor }) => ({
  backgroundColor: backgroundColor || 'var(--color-bg-surface-base)',
  position: isStickyHeaders ? 'sticky' : 'relative',
  zIndex: '10',
}));
